import React, { useEffect, useMemo, useState } from "react";
import { fmtDec, fmtInt } from "../../../shared/utils/formatters";
import { formatPercent } from "../../../shared/utils/numberLocale";
import { Select } from "../../../shared/ui/composites";
import { calendarTypeLabel } from "../../../shared/utils/calendarType";

function metricTone(own, distributed) {
  if (own <= 0) {
    return distributed > 0 ? "over" : "neutral";
  }
  const remaining = own - distributed;
  if (remaining <= 0 && distributed <= own * 1.001) return "ok";
  if (remaining < 0) return "over";
  if (distributed <= 0) return "empty";
  return "partial";
}

function barClass(tone) {
  if (tone === "ok") return "bg-emerald-500";
  if (tone === "over") return "bg-rose-500";
  if (tone === "partial") return "bg-amber-500";
  if (tone === "empty") return "bg-slate-300";
  return "bg-slate-200";
}

function overallPct(distributed, own) {
  if (own > 0) return Math.min((distributed / own) * 100, 999);
  return 0;
}

function Ratio({ distributed, own }) {
  return (
    <span className="inline-flex items-center gap-0.5 tabular-nums text-slate-700" dir="ltr">
      <span>{fmtDec(distributed, 2)}</span>
      <span className="text-slate-300">/</span>
      <span>{fmtDec(own, 2)}</span>
    </span>
  );
}

function MetricLine({ label, own, distributed }) {
  const tone = metricTone(own, distributed);
  const remaining = own - distributed;
  const pct = Math.round(overallPct(distributed, own));
  const barWidth =
    own > 0 ? Math.min((distributed / own) * 100, 100) : distributed > 0 ? 100 : 0;

  let statusText = "—";
  let statusClass = "text-slate-400";
  if (own > 0 || distributed > 0) {
    if (remaining > 0) {
      statusText = `مانده ${fmtDec(remaining, 2)}`;
      statusClass = "text-amber-700";
    } else if (remaining < 0) {
      statusText = `اضافه ${fmtDec(Math.abs(remaining), 2)}`;
      statusClass = "text-rose-600";
    } else {
      statusText = "کامل";
      statusClass = "text-emerald-700";
    }
  }

  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between gap-1 text-xs leading-none">
        <span className="shrink-0 font-semibold text-slate-500">{label}</span>
        <Ratio distributed={distributed} own={own} />
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full ${barClass(tone)}`}
          style={{ width: `${barWidth}%` }}
        />
      </div>
      <div className="flex items-center justify-between gap-1 text-xs leading-none">
        <span className={`font-bold ${statusClass}`}>{statusText}</span>
        <span className="tabular-nums text-slate-400" dir="ltr">
          {formatPercent(pct, 0)}
        </span>
      </div>
    </div>
  );
}

function GroupCard({ group, open, onToggle }) {
  const title = group.manufacturer_name
    ? `${group.manufacturer_name} · ${group.group_name}`
    : group.group_name;

  const saleTone = metricTone(group.own_sale_target, group.distributed_sale_target);
  const customerTone = metricTone(
    group.own_customer_target,
    group.distributed_customer_target,
  );

  const badge =
    saleTone === "ok" && (customerTone === "ok" || customerTone === "neutral")
      ? { text: "کامل", className: "bg-emerald-50 text-emerald-700" }
      : saleTone === "over" || customerTone === "over"
        ? { text: "اضافه", className: "bg-rose-50 text-rose-700" }
        : saleTone === "empty"
          ? { text: "خالی", className: "bg-slate-100 text-slate-500" }
          : { text: "ناقص", className: "bg-amber-50 text-amber-800" };

  const salePct = Math.round(
    overallPct(group.distributed_sale_target, group.own_sale_target),
  );

  return (
    <article className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-2.5 py-1.5 text-right"
        aria-expanded={open}
      >
        <span className="flex w-full items-center gap-2">
          <span className="text-[10px] text-slate-400" aria-hidden>
            {open ? "▾" : "▸"}
          </span>
          <span className="min-w-0 flex-1">
            <h4
              className="m-0 truncate text-xs font-bold leading-snug text-slate-800"
              title={title}
            >
              {title}
            </h4>
            <p className="m-0 truncate text-[10px] text-slate-400">
              {[
                group.unit_type,
                calendarTypeLabel(group.calendar_type),
                `${fmtInt(group.personnel_count)} نفر`,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </span>
          <span
            className="shrink-0 text-sm font-black tabular-nums text-slate-800"
            dir="ltr"
          >
            {formatPercent(salePct, 0)}
          </span>
          <span
            className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold leading-none ${badge.className}`}
          >
            {badge.text}
          </span>
        </span>
        <span className="mt-1 block h-1 overflow-hidden rounded-full bg-slate-100">
          <span
            className={`block h-full rounded-full ${barClass(saleTone)}`}
            style={{ width: `${Math.min(salePct, 100)}%` }}
          />
        </span>
      </button>
      {open ? (
        <div className="space-y-1.5 border-t border-slate-100 px-2.5 py-2">
          <MetricLine
            label="فروش"
            own={group.own_sale_target}
            distributed={group.distributed_sale_target}
          />
          <MetricLine
            label="مشتری"
            own={group.own_customer_target}
            distributed={group.distributed_customer_target}
          />
        </div>
      ) : null}
    </article>
  );
}

function averageTargetPct(summary) {
  if (!summary) return 0;
  const parts = [];
  if (summary.own_sale_total > 0) {
    parts.push(summary.distributed_sale_total / summary.own_sale_total);
  }
  if (summary.own_customer_total > 0) {
    parts.push(summary.distributed_customer_total / summary.own_customer_total);
  }
  if (!parts.length) return 0;
  return (parts.reduce((sum, part) => sum + part, 0) / parts.length) * 100;
}

function TotalProgress({ summary, open, onToggle }) {
  if (!summary) return null;
  const salePct = overallPct(summary.distributed_sale_total, summary.own_sale_total);
  const customerPct = overallPct(
    summary.distributed_customer_total,
    summary.own_customer_total,
  );
  const totalPct = averageTargetPct(summary);
  const barWidth = Math.min(totalPct, 100);

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="block w-full rounded-xl border border-indigo-100 bg-gradient-to-l from-indigo-50 to-white px-3 py-2 text-right hover:border-indigo-200"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[11px] font-bold text-indigo-700">
            <span className="ml-1 text-slate-400" aria-hidden>
              {open ? "▾" : "▸"}
            </span>
            درصد کل تارگت
          </div>
          <div className="text-2xl font-black tabular-nums leading-none text-indigo-900" dir="ltr">
            {formatPercent(totalPct, 1)}
          </div>
          {summary.staff_eligible > 0 ? (
            <div className="mt-1 text-[11px] font-semibold text-indigo-700">
              {fmtInt(summary.staff_complete)} از {fmtInt(summary.staff_eligible)} پرسنل این ماه
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
          <span>
            فروش {formatPercent(salePct, 1)}
            <span className="mr-1 tabular-nums text-slate-400" dir="ltr">
              {fmtDec(summary.distributed_sale_total, 2)}/{fmtDec(summary.own_sale_total, 2)}
            </span>
          </span>
          <span>
            مشتری {formatPercent(customerPct, 1)}
            <span className="mr-1 tabular-nums text-slate-400" dir="ltr">
              {fmtDec(summary.distributed_customer_total, 2)}/{fmtDec(summary.own_customer_total, 2)}
            </span>
          </span>
        </div>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-indigo-100">
        <div
          className="h-full rounded-full bg-indigo-500"
          style={{ width: `${barWidth}%` }}
        />
      </div>
    </button>
  );
}

/**
 * Compact accordion overview: manager own targets vs totals set for personnel.
 */
export default function ManagerTargetDistributionPanel({
  year,
  month,
  years = [],
  onYearChange,
  onMonthChange,
  calendarType = "jalali",
  onCalendarTypeChange,
  data,
  isLoading,
  showPeriodControls = true,
  periodLabel = "",
}) {
  const groups = useMemo(() => data?.groups || [], [data]);
  const summary = data?.summary || null;
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const [groupsOpen, setGroupsOpen] = useState(false);

  useEffect(() => {
    setGroupsOpen(false);
    setOpenKeys(new Set());
  }, [year, month, calendarType]);

  const allOpen = useMemo(
    () => groups.length > 0 && groups.every((g) => openKeys.has(`${g.manufacturer_id}_${g.product_group_id}`)),
    [groups, openKeys],
  );

  const toggleAll = () => {
    if (allOpen) {
      setOpenKeys(new Set());
      return;
    }
    setOpenKeys(
      new Set(groups.map((g) => `${g.manufacturer_id}_${g.product_group_id}`)),
    );
  };

  return (
    <section className="mb-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50/60">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 bg-white px-2.5 py-1.5">
        <div>
          <h3 className="m-0 text-xs font-extrabold text-slate-800">
            تارگت شما ↔ پرسنل
          </h3>
          {periodLabel ? (
            <p className="m-0 mt-0.5 text-[11px] font-semibold text-slate-500">{periodLabel}</p>
          ) : null}
        </div>
        {showPeriodControls && (
          <div className="flex items-center gap-1.5">
            <Select
              value={calendarType}
              onChange={(e) => onCalendarTypeChange?.(e.target.value)}
              className="!w-auto min-w-[5.5rem] !py-1 !text-xs"
              aria-label="نوع تقویم"
            >
              <option value="jalali">شمسی</option>
              <option value="gregorian">میلادی</option>
            </Select>
            <Select
              value={String(year)}
              onChange={(e) => onYearChange?.(e.target.value)}
              className="!w-auto min-w-[4.75rem] !py-1 !text-xs"
              aria-label="سال"
            >
              {years.map((y) => (
                <option key={y} value={String(y)}>
                  {fmtInt(y)}
                </option>
              ))}
            </Select>
            <Select
              value={String(month)}
              onChange={(e) => onMonthChange?.(e.target.value)}
              className="!w-auto min-w-[4rem] !py-1 !text-xs"
              aria-label="ماه"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={String(m)}>
                  ماه {fmtInt(m)}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="px-2.5 py-3 text-xs text-slate-500">در حال بارگذاری…</div>
      ) : (
        <>
          {summary ? (
            <div className="px-2 pt-2">
              <TotalProgress
                summary={summary}
                open={groupsOpen}
                onToggle={() => setGroupsOpen((open) => !open)}
              />
            </div>
          ) : null}
          {groups.length === 0 ? (
            <div className="px-2.5 py-3 text-xs text-slate-500">
              برای این ماه تارگتی ثبت نشده است.
            </div>
          ) : groupsOpen ? (
            <div className="p-2">
              <div className="mb-1.5 flex justify-end">
                <button
                  type="button"
                  className="rounded border border-slate-200 bg-white px-2 py-1 text-[10px] font-bold text-slate-600"
                  onClick={toggleAll}
                >
                  {allOpen ? "بستن همه" : "باز کردن همه"}
                </button>
              </div>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                {groups.map((g) => {
                  const key = `${g.manufacturer_id}_${g.product_group_id}`;
                  return (
                    <GroupCard
                      key={key}
                      group={g}
                      open={openKeys.has(key)}
                      onToggle={() =>
                        setOpenKeys((prev) => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          return next;
                        })
                      }
                    />
                  );
                })}
              </div>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}
